/**
 * Public paths
 */
export enum PublicRoutes {
  Terms = '/terms',
  Privacy = '/privacy',
  DoNotSell = '/do-not-sell',
  Help = '/help',
  Claim = '/claim/:shortCode',
  ReserveParking = '/reserve-parking',
  ParkNowV2 = '/park-now-v2',
  KioskValidation = '/kiosk-validation',
  PosReceipt = '/r/:secret',
  /** CIA QR deep link — page owns auth redirect via signInLandingPage. */
  GateAccess = '/gate-access/:uuid',
}

export const publicPaths = Object.values(PublicRoutes);

/**
 * Protected (Must be logged in aka 'authorized') paths
 */
export enum ProtectedRoutes {
  ForwardSlash = '/',
  VisitPage = '/visit',
  PaymentPage = '/payment',
  ProfilePage = '/profile',
  AccountDeletePage = '/profile/terms-and-privacy',
  MembershipPage = '/membership',
  RedeemPage = '/redeem',
  VehiclePage = '/vehicle',
  VisitReceiptPage = '/visit/:visitUuid',
  DashboardPage = '/dashboard',
  // The combined home/landing page. Like /park-now, it's viewable unauthenticated
  // (shows a Login affordance + LoginSheet) via the discoveryMapV2 allowance in CustomerRoutes.
  HomePage = '/home',
  ActivityPage = '/activity',
  ConfirmMissedEntryPage = '/confirm-entry',
  PromotionsPage = '/promo',
  RewardsPage = '/rewards',
  LinkAccountPage = '/rewards/link-account',
  ReceiptPage = '/receipt/:invoiceUuid',
  ParkNowPage = '/park-now',
  ConfirmSubscription = '/confirm-subscription',
  SubscriptionConfirmed = '/subscription-confirmed',
  ReserveParkingConfirmationPage = '/reserve-parking-confirmation',
}

export const protectedPaths = Object.values(ProtectedRoutes);

/**
 * DefaultOnboardingRoutes is the default sign up flow when no onboarding tasks are required
 */
export enum DefaultOnboardingRoutes {
  SignIn = '/sign-in',
  Verify = '/verify',
  SignUp = '/sign-up',
  AddEmail = '/sign-up/add-email',
  Vehicle = '/sign-up/add-vehicle',
  PaymentMethod = '/sign-up/add-payment',
}

export const defaultOnboardingPaths = Object.values(DefaultOnboardingRoutes);

/** Sign-in onboarding steps that have no legacy equivalent. */
export enum SignInOnboardingExtraRoutes {
  ReviewSubscription = '/sign-up/review-subscription',
  Complete = '/sign-up/complete',
}

export const signInOnboardingExtraPaths = Object.values(SignInOnboardingExtraRoutes);
export const signInOnboardingPaths = [...defaultOnboardingPaths, ...signInOnboardingExtraPaths];

/**
 * ReferralOnboardingRoutes are the routes used for onboarding members who have been referred from an external source
 */
export enum ReferralOnboardingRoutes {
  FinishAccount = '/finish-account',
}

export const referralOnboardingPaths = Object.values(ReferralOnboardingRoutes);

/**
 * StartParkingOnboardingRoutes are used when the backend sends us onboarding tasks (todos)
 */
export enum StartParkingOnboardingRoutes {
  Onboard = '/onboard',
  SelectVisitDuration = '/select-visit-duration',
  SignIn = '/auth/sign-in',
  Verification = '/auth/verify',
  AddVehicle = '/onboard/add-vehicle',
  AddEmail = '/onboard/add-email',
  SelectVehicle = '/onboard/select-vehicle',
  PaymentMethod = '/onboard/add-payment',
}

export const startParkingOnboardingPaths = Object.values(StartParkingOnboardingRoutes);

/**
 * ExpressOnboardingRoutes are the step-level routes for the new express onboarding flow (feature-flagged)
 */
export enum ExpressOnboardingRoutes {
  Landing = '/onboard',
  VerifyPhone = '/onboard/verify-phone',
  AddVehicle = '/onboard/add-vehicle',
  AddPayment = '/onboard/add-payment',
  AddEmail = '/onboard/add-email',
  ConfirmEntry = '/onboard/confirm-entry',
  Success = '/onboard/success',
}

export const expressOnboardingPaths = Object.values(ExpressOnboardingRoutes);

/**
 * /tasks is only used for the required Payment Due TODO item (user has to pay for unpaid visit)
 */

export enum TaskItemRoutes {
  Tasks = '/tasks',
}

export const taskItemPaths = Object.values(TaskItemRoutes);

export const authPaths = [...publicPaths, ...protectedPaths];
